import { Box, Link, styled, Typography } from "@mui/material";
import { useRecordContext } from "ra-core";
import { useMemo } from "react";
import { Link as RouterLink } from 'react-router-dom'
import { genUrlWithHashid } from "@/utils/hashid";
import { sanitizeFieldRestProps } from "../field";
import { useEncryptedDatabasesContext } from "./useEncryptedDatabases";
import OnDataLock from "@/icons/OnDataLock";

interface DatabaseNameFieldProps {
  className?: string,
  label?: string,
  keyMap?: {
    databaseId?: string,
    name?: string,
  },
  showLink?: boolean,
}
const defaultKeyMap = {
  databaseId: 'databaseId',
  name: 'name',
}

const EncryptedTip = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: theme.spacing(-3),
  left: theme.spacing(1),
  marginLeft: theme.spacing(-3),
  borderTop: `8px solid ${theme.palette.text.disabled}`, 
  borderRight: '8px solid transparent',
  cursor: 'pointer',

  '& + span.MuiTypography-caption': {
    transform: 'matrix(0.8, 0, 0, 0.8, -16, 0)',
  },
  '&:hover + span.MuiTypography-caption': {
    transform: 'matrix(1, 0, 0, 1, 8, 0)',
    marginLeft: theme.spacing(-1),
    '& .more': { display: 'inline' },
  },
}))

const EncryptedTipText = styled(Typography)(({theme}) => ({
  position: 'absolute',
  top: theme.spacing(-3),
  transition: 'all 0.1s',
  '& .more' : { display: 'none', float: 'right', color: theme.palette.text.disabled },
}))

const DatabaseNameField = (
  props: DatabaseNameFieldProps
) => {
  const { className, keyMap: inMap, showLink, ...rest } = props;
  const record = useRecordContext(props) as any;
  const { encryptedDbMap } = useEncryptedDatabasesContext()
  
  return useMemo(() => {
    const keyMap = { ...defaultKeyMap, ...inMap }
    const dbId = record[keyMap.databaseId]
    const name = record[keyMap.name]
    const showTip = Boolean(encryptedDbMap && encryptedDbMap[dbId])

    return record ? (
      <Box sx={{ width: '100%', position: 'relative' }}>
        {showTip &&
          <>
            <EncryptedTip className="encryptedDbTip" />
            <EncryptedTipText variant="caption" className="encryptedDbTipText">
              <span><OnDataLock transform="scale(0.6)" fontSize="small" color="disabled" /></span>
              <span className="more"> Encrypted Database</span>
            </EncryptedTipText>
          </>
        }
        <Typography
            component="span"
            variant="body2"
            className={className}
            {...sanitizeFieldRestProps(rest)}
        >
          {showLink ?
            <Link
              underline="hover"
              component={RouterLink}
              to={genUrlWithHashid(`/databases/$0/show`, dbId || 0, name)}
            >
              {name}
            </Link> :
            name
          }
        </Typography>
      </Box>
    ) : null
  
  }, [className, record, inMap, rest, encryptedDbMap]);
}

export default DatabaseNameField;
